<template>
    <div class="otp-page">
        <div class="otp-card">
            <h1 class="otp-title">ยืนยันรหัส 6 หลัก</h1>
            <p class="otp-subtitle">กรอกรหัสเพื่อเข้าสู่หน้าหลัก</p>

            <div class="otp-inputs">
                <input
                    v-for="(digit, index) in otpDigits"
                    :key="index"
                    :ref="(el) => setInputRef(el, index)"
                    :value="digit"
                    class="otp-input"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    autocomplete="one-time-code"
                    @input="onInput($event, index)"
                    @keydown="onKeyDown($event, index)"
                    @paste="onPaste"
                />
            </div>

            <button
                class="otp-submit"
                :disabled="!isOtpComplete"
                @click="submitOtp"
            >
                ยืนยันและเข้าสู่หน้าหลัก
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "../services/axios";

const OTP_LENGTH = 6;

export default {
    name: "OtpVerification",
    data() {
        return {
            otpDigits: Array(OTP_LENGTH).fill(""),
            inputRefs: [],
        };
    },
    computed: {
        isOtpComplete() {
            return this.otpDigits.every((digit) => digit !== "");
        },
        ...mapGetters(["Profile"]),
    },
    mounted() {
        this.focusInput(0);
        if (!this.Profile.userId) {
            this.$router.push("/");
        }
    },
    methods: {
        setInputRef(el, index) {
            if (el) {
                this.inputRefs[index] = el;
            }
        },
        focusInput(index) {
            const input = this.inputRefs[index];
            if (input) {
                input.focus();
                input.select();
            }
        },
        onInput(event, index) {
            const value = String(event.target.value || "").replace(/\D/g, "");
            const digit = value.slice(-1);

            this.otpDigits[index] = digit;
            event.target.value = digit;

            if (digit && index < OTP_LENGTH - 1) {
                this.focusInput(index + 1);
            }
        },
        onKeyDown(event, index) {
            if (
                event.key === "Backspace" &&
                !this.otpDigits[index] &&
                index > 0
            ) {
                this.focusInput(index - 1);
                return;
            }

            if (event.key === "ArrowLeft" && index > 0) {
                event.preventDefault();
                this.focusInput(index - 1);
                return;
            }

            if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
                event.preventDefault();
                this.focusInput(index + 1);
            }
        },
        onPaste(event) {
            event.preventDefault();
            const pasted = event.clipboardData
                .getData("text")
                .replace(/\D/g, "")
                .slice(0, OTP_LENGTH);

            pasted.split("").forEach((char, index) => {
                this.otpDigits[index] = char;
            });

            const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
            this.focusInput(nextIndex);
        },
        submitOtp() {
            if (!this.isOtpComplete) {
                return;
            }

            axios
                .post("/v2/access", {
                    line_user_id: this.Profile.userId,
                    access_key: this.otpDigits.join(""),
                })
                .then((resp) => {
                    console.log(resp);
                    if (resp.data.status) {
                        this.$store.dispatch("setAccess", true);
                        this.$router.replace("/");
                    } else {
                        this.$swal({
                            title: "รหัสไม่ถูกต้อง",
                            text: "ไม่สามารถเข้าสู่หน้าหลักได้",
                            icon: "error",
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                    this.$swal({
                        title: "เกิดข้อผิดผลาด",
                        text: "ไม่สามารถตรวจสอบข้อมูลได้",
                        icon: "error",
                    });
                });
        },
    },
};
</script>

<style scoped>
.otp-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: radial-gradient(
        circle at 15% 20%,
        #f2f7f7 0%,
        #d6e4e5 45%,
        #c3d8da 100%
    );
}

.otp-card {
    width: 100%;
    max-width: 460px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(153, 171, 172, 0.35);
    border-radius: 22px;
    box-shadow: 0 20px 20px rgba(61, 88, 90, 0.18);
    padding: 2rem 1.5rem;
}

.otp-title {
    margin: 0;
    text-align: center;
    color: #1f3e40;
    font-size: 1.5rem;
    font-weight: 700;
}

.otp-subtitle {
    margin: 0.5rem 0 1.5rem;
    text-align: center;
    color: #4e6e70;
    font-size: 0.95rem;
}

.otp-inputs {
    display: grid;
    grid-template-columns: repeat(6, minmax(40px, 1fr));
    gap: 0.55rem;
    margin-bottom: 1.5rem;
}

.otp-input {
    height: 52px;
    border: 1.5px solid #b8cbcc;
    border-radius: 12px;
    text-align: center;
    font-size: 1.5rem;
    color: #204547;
    background-color: #ffffff;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.otp-input:focus {
    outline: none;
    border-color: #2f7c80;
    box-shadow: 0 0 0 4px rgba(47, 124, 128, 0.14);
}

.otp-submit {
    width: 100%;
    border: 0;
    border-radius: 12px;
    height: 48px;
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
    background: #2dce89;
    cursor: pointer;
    transition: transform 0.15s, opacity 0.15s;
}

.otp-submit:hover:not(:disabled) {
    transform: translateY(-1px);
}

.otp-submit:disabled {
    opacity: 0.8;
    cursor: not-allowed;
}

@media (max-width: 460px) {
    .otp-card {
        padding: 1.5rem 1rem;
    }

    .otp-input {
        height: 48px;
        font-size: 1.35rem;
    }
}
</style>
